import {
  DbStatus,
  GroupTag,
  Horse,
  HorseActionSuccessTypeEnum,
} from '@sleipai/sleip-models';
import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import { filter, includes } from 'lodash';
import {
  horsesFeatureKey,
  selectAllHorsesAdapter,
  State,
} from '../reducers/horses.reducer';
import * as fromRouterSelector from '../../router-store/router.selectors';
import { PAGE_SIZE, QueryConfig, dynamicSort } from '../../helpers';
import {
  ClientWebAppProjectEnum,
  ClientWebAppProjectType,
} from '../../env-configuration';

export const selectHorsesState: MemoizedSelector<object, State> =
  createFeatureSelector<State>(horsesFeatureKey);

export const isLoading = (state: State): boolean => state.loading;
export const isLoaded = (state: State): boolean => state.loaded;
export const totalHorseCount = (state: State): number => state.totalHorseCount;
export const numberOfPages = (state: State): number => state.numberOfPages;
export const horseSaved = (state: State): boolean => state.horseSaved;
export const horseError = (state: State): string | null => state.error;
export const horseRemoved = (state: State): boolean => state.horseRemoved;
export const horseActionSuccessType = (
  state: State,
): HorseActionSuccessTypeEnum | null => state.horseActionSuccessType;

export const batchStopShareFailedIds = (
  state: State,
): string[] | null => state.batchStopShareFailedIds;

export const batchDeleteFailedIds = (
  state: State,
): string[] | null => state.batchDeleteFailedIds;

export const batchCloneFailedIds = (
  state: State,
): string[] | null => state.batchCloneFailedIds;

export const selectIsLoading: MemoizedSelector<object, boolean> =
  createSelector(selectHorsesState, isLoading);

export const selectIsLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectHorsesState,
  isLoaded,
);

export const selectTotalHorseCount: MemoizedSelector<object, number> =
  createSelector(selectHorsesState, totalHorseCount);

export const selectNumberOfPages: MemoizedSelector<object, number> =
  createSelector(selectHorsesState, numberOfPages);

export const selectHorsesHasMore: MemoizedSelector<object, boolean> =
  createSelector(selectHorsesState, (state: State) => state.hasMoreHorses);

export const selectLastSearchIds: MemoizedSelector<object, string[] | null> =
  createSelector(selectHorsesState, (state: State) => state.lastSearchIds);

export const selectHorseSaved: MemoizedSelector<object, boolean> =
  createSelector(selectHorsesState, horseSaved);

export const selectHorseError: MemoizedSelector<object, string | null> =
  createSelector(selectHorsesState, horseError);

export const selectHorseRemoved: MemoizedSelector<object, boolean> =
  createSelector(selectHorsesState, horseRemoved);

export const selectAllHorses: (state: object) => any[] = createSelector(
  selectHorsesState,
  selectAllHorsesAdapter,
);

export const selectHorseWithOwner = (owner: string) =>
  createSelector(selectAllHorses, (horses: any[]) => {
    if (horses) {
      return horses.filter((horse) => horse.owner === owner);
    } else {
      return null;
    }
  });

export const selectHorsesAssociatedWithUser = (uid: string) =>
  createSelector(selectAllHorses, (horses: Horse[]) => {
    if (horses) {
      return horses.filter((obj: Horse) => {
        const rolesUser = Object.keys(obj?.roles);
        const roleIncludesUser = rolesUser.includes(uid);
        if (roleIncludesUser) {
          return obj;
        }
        return null;
      });
    } else {
      return null;
    }
  });

export const selectHorsesWithHorseIds = (horseIds: string[]) =>
  createSelector(selectAllHorses, (horses: Horse[]) => {
    if (horses && horseIds) {
      const filteredHorsesByIds = filter(horses, (horse) =>
        includes(horseIds, horse.id),
      );
      return filteredHorsesByIds;
    } else {
      return null;
    }
  });

export const selectSingleHorse = (passedHorseId?: string) =>
  createSelector(
    selectAllHorses,
    fromRouterSelector.selectRouteNestedParam('horseId'),
    (horses: Horse[], urlParamHorseId: string) => {
      const horseId = passedHorseId ?? urlParamHorseId;

      if (horses && horses.length !== 0) {
        return horses.find((horse) => horse.id === horseId) ?? null;
      } else {
        return null;
      }
    },
  );

export const selectHorsesInGroup = (groupId: string) =>
  createSelector(selectAllHorses, (horses: Horse[]) => {
    if (horses) {
      return horses.filter(
        (horse: Horse) => horse.group && horse.group.id === groupId,
      );
    } else {
      return null;
    }
  });

export const selectHorseActionSuccessType = createSelector(
  selectHorsesState,
  horseActionSuccessType,
);

export const selectBatchStopShareFailedIds = createSelector(
  selectHorsesState,
  batchStopShareFailedIds,
);

export const selectBatchDeleteFailedIds = createSelector(
  selectHorsesState,
  batchDeleteFailedIds,
);

export const selectBatchCloneFailedIds = createSelector(
  selectHorsesState,
  batchCloneFailedIds,
);

export const selectHorsesListPage = (
  queryConfig: QueryConfig,
  clientWebAppProject?: ClientWebAppProjectType,
) => {
  const {
    page,
    orderBy = 'creationDate',
    orderDirection = 'asc',
    pageSize = PAGE_SIZE,
    searchQuery,
    filterMap,
  } = queryConfig;

  const hasActiveSearch = !!(searchQuery && searchQuery.trim());
  const hasActiveFilters = !!(filterMap && Array.from(filterMap.entries()).some(([key, val]) => {
    if (key === 'dbStatus' && val === DbStatus.ACTIVE) {
      return false;
    }
    if (key === `${ClientWebAppProjectEnum.VET_WEB_APP}-filter`) {
      const arr = val ? Array.from(val as any[]) : [];
      return arr.length > 0 && !arr.includes('all');
    }
    return val !== undefined && val !== null && (Array.isArray(val) ? val.length > 0 : true);
  }));
  const isSearchOrFilterActive = hasActiveSearch || hasActiveFilters;

  return createSelector(
    selectAllHorses,
    selectLastSearchIds,
    selectIsLoading,
    (horses: Horse[] | null | undefined, lastSearchIds: string[] | null, isLoading: boolean) => {
      // -- handle case of no horses availble
      if (isLoading || !horses || horses.length === 0) {
        return [];
      }

      if (isSearchOrFilterActive && lastSearchIds) {
        const horseMap = new Map(horses.map(h => [h.id, h]));
        const ordered = lastSearchIds
          .map(id => horseMap.get(id))
          .filter((h): h is Horse => !!h);
        return ordered;
      }

      let filteredHorses = [...horses];
      // -- Filter start

      const filterByDbStatus = queryConfig.filterMap?.get('dbStatus') as string;
      if (filterByDbStatus) {
        filteredHorses = filteredHorses.filter(
          (horse) => horse.dbStatus === filterByDbStatus,
        );
      }

      // 1. Filter out all active horses exit out early if we don't have any active horses.
      if (clientWebAppProject !== ClientWebAppProjectEnum.VET_WEB_APP) {
        filteredHorses = filteredHorses.filter(
          (horse) => horse.dbStatus === DbStatus.ACTIVE,
        );
        if (!filteredHorses.length) return [];
      }

      // 2. Filter by Search Query (if provided)

      if (searchQuery) {
        // Breakout regex and cleaned query for easier reading
        const cleanedQuery = searchQuery
          .trim()
          .replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        if (cleanedQuery) {
          const regex = new RegExp(cleanedQuery, 'gi');
          filteredHorses = filteredHorses.filter((horse) => {
            // Check match for horse neame
            const nameMatch = horse.name && horse.name.match(regex);
            // Check match for horse owner name
            const ownerMatch =
              horse.horseOwnerName && horse.horseOwnerName.match(regex);
            return nameMatch || ownerMatch;
          });
          if (!filteredHorses.length) return [];
        }
      }

      // 3. filter for based on filterMap

      filterMap?.forEach((value: unknown, key: string) => {
        // -- Filter based on tags
        if (key === 'tags') {
          if (Array.isArray(value) && value.length > 0) {
            filteredHorses = filteredHorses.filter((horse) => {
              if (Array.isArray(horse.tags) && horse.tags.length > 0) {
                const hasMatchingTag = horse.tags.some(
                  (tagObject: GroupTag) =>
                    tagObject &&
                    typeof tagObject.id === 'string' &&
                    value.includes(tagObject.id),
                );
                return hasMatchingTag;
              }
              return false;
            });

            // if (!filteredHorses.length) return [];
          }
        }

        // -- filter based on shared
        if (key === 'shared') {
          filteredHorses = filteredHorses.filter((horse: Horse) => {
            return horse.group?.remoteShareActive;
          });
        }

        if (key === 'visibleBy') {
          filteredHorses = filteredHorses.filter((horse: Horse) => {
            const horseRoleValues = Object.keys(horse.roles);
            return horseRoleValues.includes(value as string);
          });
        }

        if (key === 'group') {
          filteredHorses = filteredHorses.filter((horse: Horse) => {
            return horse.group?.id === value;
          });
        }

        if (key === 'filterByHorseObjectIds') {
          const filterByHorseObjectIds = value as string[];
          filteredHorses = filteredHorses.filter(
            (horse) => horse.id && filterByHorseObjectIds.includes(horse.id),
          );
        }

        if (key === `${ClientWebAppProjectEnum.VET_WEB_APP}-filter`) {
          const filterValues = Array.from<string>(value as []);
          if (!filterValues.includes('all')) {
            if (
              ['shared', 'remote-monitoring'].every((filter) =>
                filterValues.includes(filter),
              )
            ) {
              filteredHorses = filteredHorses.filter((horse: Horse) => {
                const horseRoleValues = Object.values(horse.roles);
                return (
                  horseRoleValues.filter(
                    (role) => role === 'vet' || role === 'EHP',
                  ).length >= 2 && horseRoleValues.includes('recorder')
                );
              });
            } else {
              if (
                filterValues.includes('shared') &&
                !filterValues.includes('remote-monitoring')
              ) {
                filteredHorses = filteredHorses.filter((horses: Horse) => {
                  const horseRoleValues = Object.values(horses.roles);
                  return (
                    horseRoleValues.length > 1 &&
                    !horseRoleValues.includes('recorder')
                  );
                });
              }
              if (
                filterValues.includes('remote-monitoring') &&
                !filterValues.includes('shared')
              ) {
                filteredHorses = filteredHorses.filter((horses: Horse) => {
                  const horseRoleValues = Object.values(horses.roles);
                  return (
                    horseRoleValues?.length > 1 &&
                    horseRoleValues.some((role: string) => role === 'recorder')
                  );
                });
              }
            }
            if (filterValues.includes(DbStatus.REMOVED)) {
              filteredHorses = filteredHorses.filter(
                (horse) => horse.dbStatus === DbStatus.REMOVED,
              );
            }
          }
        }
      });

      // -- Start Sorting
      const sortedHorses = [...filteredHorses].sort(
        dynamicSort(orderBy, orderDirection),
      );

      // -- Start Pagination
      const endIndex = pageSize * (page + 1);
      return sortedHorses.slice(0, endIndex);
    },
  );
};
